import { CpsUserAllowedActions } from '@cs-cms/types';
import React from 'react';

export interface PreviewDocument {
  contentType: string;
  documentId: string;
  permissions: CpsUserAllowedActions[];
}

interface DocumentPreviewContextValue {
  previewDocument: PreviewDocument | undefined;
  openDocumentPreview: (document: PreviewDocument) => void;
  closeDocumentPreview: () => void;
}

const DocumentPreviewContext = React.createContext<DocumentPreviewContextValue | undefined>(undefined);

interface DocumentPreviewProviderProps {
  children: React.ReactNode;
}

export const DocumentPreviewProvider: React.FC<DocumentPreviewProviderProps> = ({ children }) => {
  const [previewDocument, setPreviewDocument] = React.useState<PreviewDocument | undefined>(undefined);

  const openDocumentPreview = React.useCallback((document: PreviewDocument): void => {
    setPreviewDocument(document);
  }, []);

  const closeDocumentPreview = React.useCallback((): void => {
    setPreviewDocument(undefined);
  }, []);

  const value = React.useMemo(
    (): DocumentPreviewContextValue => ({
      previewDocument,
      openDocumentPreview,
      closeDocumentPreview
    }),
    [previewDocument, openDocumentPreview, closeDocumentPreview]
  );

  return <DocumentPreviewContext.Provider value={value}>{children}</DocumentPreviewContext.Provider>;
};

export const useDocumentPreviewContext = (): DocumentPreviewContextValue => {
  const context = React.useContext(DocumentPreviewContext);
  if (context === undefined) {
    throw new Error('useDocumentPreviewContext must be used within a DocumentPreviewProvider');
  }
  return context;
};
